/**
 * Game Loop - Fixed Timestep Physics, Variable Render
 *
 * Core game loop optimized for 60fps with:
 * - Fixed timestep physics (deterministic)
 * - Variable render rate (smooth visuals)
 * - Frame time accumulator
 * - Performance monitoring
 * - Automatic throttling when tab inactive
 */

/**
 * Max fixed-physics steps executed in a single rendered frame. Bounds the work
 * per frame (prevents a spiral of death) without discarding simulated time —
 * any steps beyond this stay in the accumulator and run next frame. At 60 fps
 * a frame needs 1 step; this only engages when catching up after a hitch.
 */
const MAX_PHYSICS_STEPS_PER_FRAME = 10;

export interface GameLoopCallbacks {
  /** Fixed update for physics - called at fixed intervals (default 60Hz) */
  fixedUpdate: (fixedDeltaTime: number) => void;
  /** Variable update for game logic - called every frame */
  update: (deltaTime: number) => void;
  /** Render - called every frame after update */
  render: (interpolation: number) => void;
}

export interface GameLoopConfig {
  /** Target physics updates per second (default: 60) */
  fixedFps?: number;
  /** Maximum frame time to prevent spiral of death (default: 250ms) */
  maxFrameTime?: number;
  /** Enable performance monitoring (default: true in dev) */
  enablePerfMonitor?: boolean;
}

export interface PerformanceStats {
  fps: number;
  frameTime: number;
  physicsTime: number;
  renderTime: number;
  updateTime: number;
  physicsUpdatesPerFrame: number;
}

export class GameLoop {
  private callbacks: GameLoopCallbacks;
  private isRunning = false;
  private animationFrameId: number | null = null;

  // Timing
  private fixedDeltaTime: number;
  private maxFrameTime: number;
  private lastTime = 0;
  private accumulator = 0;

  // Lockstep stepping (cross-device dice determinism). While `fixedStepLock`
  // returns true (a die is airborne) the loop runs exactly ONE fixed step per
  // rendered frame instead of the wall-clock accumulator, so every client runs
  // the SAME step count for a throw regardless of frame rate. `lockAccumulator`
  // gates the step rate to fixedFps on high-refresh displays; below fixedFps it
  // is one step per frame (the roll plays slow-motion but stays in lockstep).
  private fixedStepLock: (() => boolean) | null = null;
  private lockAccumulator = 0;
  private wasLockstep = false;

  // Performance monitoring
  private enablePerfMonitor: boolean;
  private frameCount = 0;
  private fpsTime = 0;
  private stats: PerformanceStats = {
    fps: 0,
    frameTime: 0,
    physicsTime: 0,
    renderTime: 0,
    updateTime: 0,
    physicsUpdatesPerFrame: 0,
  };

  // Visibility handling
  private isVisible = true;
  private boundHandleVisibility: () => void;
  private boundTick: (time: number) => void;

  constructor(callbacks: GameLoopCallbacks, config: GameLoopConfig = {}) {
    this.callbacks = callbacks;

    const fixedFps = config.fixedFps ?? 60;
    this.fixedDeltaTime = 1 / fixedFps;
    this.maxFrameTime = (config.maxFrameTime ?? 250) / 1000;
    this.enablePerfMonitor = config.enablePerfMonitor ?? (import.meta.env?.DEV ?? false);

    // Bind methods for event handlers
    this.boundHandleVisibility = this.handleVisibility.bind(this);
    this.boundTick = this.tick.bind(this);
  }

  /**
   * Start the game loop
   */
  start(): void {
    if (this.isRunning) return;

    this.isRunning = true;
    this.lastTime = performance.now() / 1000;
    this.accumulator = 0;
    this.lockAccumulator = 0;
    this.wasLockstep = false;
    this.frameCount = 0;
    this.fpsTime = this.lastTime;

    // Listen for visibility changes
    document.addEventListener('visibilitychange', this.boundHandleVisibility);

    // Start loop
    this.animationFrameId = requestAnimationFrame(this.boundTick);
  }

  /**
   * Stop the game loop
   */
  stop(): void {
    if (!this.isRunning) return;

    this.isRunning = false;

    if (this.animationFrameId !== null) {
      cancelAnimationFrame(this.animationFrameId);
      this.animationFrameId = null;
    }

    document.removeEventListener('visibilitychange', this.boundHandleVisibility);
  }

  /**
   * Provide a predicate the loop checks every frame. While it returns true the
   * loop steps physics ONE fixed step per rendered frame (frame-rate-independent
   * step count) instead of the wall-clock accumulator. Used to make in-flight
   * dice simulate identically on every client. Pass null to disable.
   */
  setFixedStepLock(predicate: (() => boolean) | null): void {
    this.fixedStepLock = predicate;
  }

  /**
   * Main tick function - called every animation frame
   */
  private tick(timeMs: number): void {
    if (!this.isRunning) return;

    const time = timeMs / 1000;
    let frameTime = time - this.lastTime;
    this.lastTime = time;

    // Clamp frame time to prevent spiral of death
    if (frameTime > this.maxFrameTime) {
      frameTime = this.maxFrameTime;
    }

    // Skip update if tab is hidden
    if (!this.isVisible) {
      this.animationFrameId = requestAnimationFrame(this.boundTick);
      return;
    }

    const frameStart = performance.now();
    let physicsTime = 0;
    let updateTime = 0;
    let physicsUpdates = 0;

    // Choose the stepping mode. While a die is airborne (`fixedStepLock`) we
    // run lockstep: exactly ONE fixed step per rendered frame, so the step
    // COUNT from throw to rest is identical on every client regardless of
    // frame rate — the dice simulation is then bit-identical across devices.
    // The rest of the time we use the wall-clock accumulator so non-dice
    // physics stays real-time.
    const lockstep = this.fixedStepLock?.() ?? false;

    const physicsStart = performance.now();
    if (lockstep) {
      if (!this.wasLockstep) {
        // Entering lockstep: drain the real-time accumulator and prime the
        // lock accumulator so this frame steps immediately (the die was just
        // released).
        this.accumulator = 0;
        this.lockAccumulator = this.fixedDeltaTime;
      }
      // One fixed step per frame, gated so the step RATE never exceeds fixedFps
      // on high-refresh displays. Below fixedFps it is one step per frame: the
      // roll plays slow-motion but runs the SAME step count, which is what
      // keeps every client's simulation in sync.
      this.lockAccumulator += frameTime;
      if (this.lockAccumulator >= this.fixedDeltaTime) {
        this.lockAccumulator -= this.fixedDeltaTime;
        // Never bank more than one step of credit → hard cap of one step/frame.
        if (this.lockAccumulator > this.fixedDeltaTime) {
          this.lockAccumulator = this.fixedDeltaTime;
        }
        this.callbacks.fixedUpdate(this.fixedDeltaTime);
        physicsUpdates = 1;
      }
    } else {
      if (this.wasLockstep) {
        // Leaving lockstep: don't pour the wall-clock time that elapsed during
        // slow-motion stepping into the accumulator — that would burst a long
        // catch-up. Start the real-time accumulator fresh.
        this.accumulator = 0;
      }
      // Accumulate time for fixed update
      this.accumulator += frameTime;

      // Fixed timestep physics updates
      while (this.accumulator >= this.fixedDeltaTime) {
        this.callbacks.fixedUpdate(this.fixedDeltaTime);
        this.accumulator -= this.fixedDeltaTime;
        physicsUpdates++;

        // Cap steps per frame to bound work, but DO NOT discard the leftover
        // accumulator. Zeroing it here drops un-simulated time, which desyncs
        // this client's physics step count from every other client watching the
        // same roll — the chaotic 3-dice simulation then diverges and the dice
        // land differently (then snap to the shooter's authoritative pose).
        // Carrying the remainder defers the extra steps to the next frame
        // instead of dropping them; at any playable frame rate the backlog
        // self-clears within a frame or two, so step counts stay identical
        // across devices. The maxFrameTime clamp above still bounds a single
        // catastrophic hitch (tab background / debugger pause) so we never try
        // to simulate seconds of physics in one frame.
        if (physicsUpdates >= MAX_PHYSICS_STEPS_PER_FRAME) {
          break;
        }
      }
    }
    this.wasLockstep = lockstep;
    physicsTime = performance.now() - physicsStart;

    // Variable update (game logic)
    const updateStart = performance.now();
    this.callbacks.update(frameTime);
    updateTime = performance.now() - updateStart;

    // Render with interpolation (use whichever accumulator drove this frame).
    const interpolation =
      (lockstep ? this.lockAccumulator : this.accumulator) / this.fixedDeltaTime;
    const renderStart = performance.now();
    this.callbacks.render(interpolation);
    const renderTime = performance.now() - renderStart;

    // Performance monitoring
    if (this.enablePerfMonitor) {
      this.frameCount++;
      if (time - this.fpsTime >= 1.0) {
        this.stats.fps = this.frameCount / (time - this.fpsTime);
        this.stats.frameTime = performance.now() - frameStart;
        this.stats.physicsTime = physicsTime;
        this.stats.updateTime = updateTime;
        this.stats.renderTime = renderTime;
        this.stats.physicsUpdatesPerFrame = physicsUpdates;

        this.frameCount = 0;
        this.fpsTime = time;
      }
    }

    // Schedule next frame
    this.animationFrameId = requestAnimationFrame(this.boundTick);
  }

  /**
   * Handle visibility change (tab switch)
   */
  private handleVisibility(): void {
    this.isVisible = !document.hidden;

    if (this.isVisible) {
      // Reset timing when returning to tab
      this.lastTime = performance.now() / 1000;
      this.accumulator = 0;
      this.lockAccumulator = 0;
    }
  }

  /**
   * Get current performance stats
   */
  getStats(): Readonly<PerformanceStats> {
    return this.stats;
  }

  /**
   * Check if loop is running
   */
  get running(): boolean {
    return this.isRunning;
  }

  /**
   * Get the fixed delta time (for physics)
   */
  get fixedDelta(): number {
    return this.fixedDeltaTime;
  }
}
